import React, { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import AppShell from '@/components/AppShell';
import AppSidebar, { type AppView } from '@/components/AppSidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import {
  Plus, Search, ChevronRight, ChevronLeft, ChevronDown, Users, Shield, RotateCcw,
  Building2, ScrollText, FolderOpen, Trash2, KeyRound, Pencil, Mail, RefreshCw, UserX,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  type OrgNode, type OrgNodeType, type FlagKey, type ClientRef, type ClientSearchHit,
  FLAG_META, findNode, findPath, parentOf, rollupLine, typeLabel, initials,
  flattenDownline, hasFlag, flagSource, rollup, memberCount, ancestorIds, searchClients, allClients,
} from '@/components/org/hierarchyTree';
import {
  useHierarchyTree, setNodeFlag, clearNodeFlag, fetchClientScenarios,
  useDownlineInvites, resendDownlineInvite, revokeDownlineInvite, inviteKindLabel, type DownlineInvite,
  setMemberStatus, setMemberClientBook, setMemberRole, renameOrg, renameMember, sendPasswordReset,
} from '@/components/org/hierarchyLive';
import { supabase } from '@/lib/safeSupabase';
import type { TaxScenario } from '@/tax/types';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { sanitizeError } from '@/lib/errorMessages';
import { logClientError } from '@/lib/errorLogger';
import { inviteStatus, partitionInvites, countPending, INVITE_TTL_DAYS } from '@/components/org/inviteStatus';
import tacticalLogo from '@/assets/tactical-tax-logo.png';
import { MoveDialog, BranchActionDialog } from '@/components/org/BranchDialogs';
import { TutorialProvider, useTutorial } from '@/tutorial/TutorialContext';
import { isTourPopover } from '@/tutorial/engine';
import { SAMPLE_DOWNLINE, SAMPLE_NOTICE } from '@/tutorial/sampleData';
import { buildHierarchyTree } from '@/components/org/hierarchyLive';
import { deactivateMember } from '@/lib/deactivation';

// ---------------------------------------------------------------------------
// Tile colors per node type
// ---------------------------------------------------------------------------
const TILE: Record<OrgNodeType, { bg: string; text: string; icon: React.ReactNode }> = {
  platform: { bg: 'bg-[#0E1729]', text: 'text-white', icon: <Shield className="h-5 w-5" /> },
  imo:      { bg: 'bg-[#2D4B6E]', text: 'text-white', icon: <Building2 className="h-5 w-5" /> },
  agency:   { bg: 'bg-primary',    text: 'text-white', icon: <Building2 className="h-5 w-5" /> },
  team:     { bg: 'bg-primary/10', text: 'text-primary', icon: <Users className="h-5 w-5" /> },
  agent:    { bg: 'bg-muted',      text: 'text-[#2D4B6E]', icon: <Users className="h-5 w-5" /> },
};

const TYPE_BADGE: Record<OrgNodeType, { bg: string; text: string }> = {
  platform: { bg: 'bg-[#0E1729]', text: 'text-white' },
  imo:      { bg: 'bg-[#2D4B6E]', text: 'text-white' },
  agency:   { bg: 'bg-primary/15', text: 'text-primary' },
  team:     { bg: 'bg-primary/10', text: 'text-primary' },
  agent:    { bg: 'bg-muted', text: 'text-muted-foreground' },
};

/** How the level filter names what it lists: "1 team", "4 agencies". */
const LEVEL_WORDS: Record<string, [string, string]> = {
  imo: ['IMO', 'IMOs'], agency: ['agency', 'agencies'], team: ['team', 'teams'],
  agent: ['agent', 'agents'], clients: ['client', 'clients'],
};

const AUDIT_DOT: Record<string, string> = {
  role: 'bg-primary', flag: 'bg-amber-400', invite: 'bg-emerald-500',
  sub: 'bg-[#2D4B6E]', dup: 'bg-primary', add: 'bg-emerald-500', status: 'bg-destructive',
};

// ---------------------------------------------------------------------------
// Add-menu options per node type (spec §4.1)
// ---------------------------------------------------------------------------
function addOptions(nodeType: OrgNodeType): { value: string; label: string; needsEmail: boolean }[] {
  switch (nodeType) {
    // Under Tactical Tax the only thing that belongs is a partner IMO. Our own
    // people are invited from Organization → Team, never from here, so an
    // invite from the platform level can't land someone in a vacuum.
    case 'platform': return [
      { value: 'imo', label: 'IMO', needsEmail: false },
    ];
    case 'imo': return [
      { value: 'imo-admin', label: 'IMO Admin', needsEmail: true },
      { value: 'agency', label: 'Agency', needsEmail: false },
      { value: 'team', label: 'Team', needsEmail: false },
      { value: 'agent', label: 'Agent', needsEmail: true },
    ];
    case 'agency': return [
      { value: 'agency-admin', label: 'Agency Admin', needsEmail: true },
      { value: 'team', label: 'Team', needsEmail: false },
      { value: 'agent', label: 'Agent', needsEmail: true },
    ];
    case 'team': return [
      { value: 'team-admin', label: 'Team Admin', needsEmail: true },
      { value: 'agent', label: 'Agent', needsEmail: true },
    ];
    default: return [];
  }
}

/** What each Add choice actually creates, named after the node it goes into. */
function addHelp(kind: string, target: string): string {
  // A new IMO is explained by the dialog's own description.
  if (kind === 'imo') return '';
  if (kind.endsWith('-admin')) return `Helps run ${target} and everything under it. Joins ${target}; no new branch.`;
  if (kind === 'agency') return `A branch inside ${target}, run by this person. Can hold teams and agents.`;
  if (kind === 'team') return `A small group inside ${target}, led by this person. Can hold agents.`;
  if (kind === 'agent') return `An advisor who joins ${target} and works their own clients.`;
  return '';
}

/** An agency or team is a branch of the node it's added to, so the adder names it. */
const namedByAdder = (kind: string) => kind === 'agency' || kind === 'team';

/**
 * One client hit in the downline search results. Always names the owning
 * agent, since "whose is this?" is half of why an owner is looking.
 */
function ClientRow({
  hit,
  root,
  busy,
  onOpen,
}: {
  hit: ClientSearchHit;
  root: OrgNode | null;
  busy: boolean;
  onOpen: () => void;
}) {
  const path = root ? findPath(root, hit.agent.id) : null;
  const crumb = path ? path.filter(n => n.type !== 'platform').map(n => n.name).join(' › ') : hit.agent.name;
  return (
    <div
      onClick={onOpen}
      className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-accent/50 border-b last:border-b-0"
    >
      <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 bg-primary/10 text-primary">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderOpen className="h-4 w-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground truncate">{hit.client.name}</span>
          <span className="text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded bg-primary/10 text-primary">
            Client
          </span>
        </div>
        <p className="text-[11px] text-muted-foreground truncate mt-0.5">
          {crumb} · edited {hit.client.lastEdit}
        </p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
    </div>
  );
}

// ===========================================================================
// MAIN COMPONENT — live data via get_hierarchy_tree; viewer anchored to their
// real position (platform staff at the root, org admins at their own org).
// ===========================================================================
export function TeamManagementContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isImpersonating, startImpersonation } = useImpersonation();
  const { isSuperAdmin, isPlatformAdmin, isOrgOwner } = useAuth();
  const { data: liveData, isLoading, error, refetch } = useHierarchyTree();

  // Sample downline (Drew, 2026-09-30): the Downline walkthrough on a tree with
  // nobody in it shows a made-up one instead, so there is something to walk
  // through. Whenever that walkthrough plays (Drew, 2026-10-01: the tour runs
  // on mock data, full stop — an owner's one real agent has none of the
  // agencies, invites or switched-off strategies the tour points at); the real
  // tree is back the moment it ends. Every action on the sample is refused
  // with a note; the flag switches work locally like the sandbox and persist
  // nothing.
  const { activeWalkthroughId } = useTutorial();
  const sampleActive = activeWalkthroughId === 'downline';
  const sampleData = useMemo(() => buildHierarchyTree(SAMPLE_DOWNLINE), []);
  const data = sampleActive ? sampleData : liveData;
  const isSandbox = isImpersonating || sampleActive;
  /** True (and says so) when an action was clicked on the sample. */
  const refuseSample = useCallback(() => {
    if (!sampleActive) return false;
    toast.success('This is sample data', { description: 'When the walkthrough ends, do this for real on your own downline.' });
    return true;
  }, [sampleActive]);

  // --- Navigation state — persisted so the position survives round trips
  // (opening a client and coming back, exiting impersonation, refresh). Stale
  // ids degrade gracefully: an id the tree no longer contains falls back to
  // the root.
  //
  // The whole downline renders as one expandable tree table. `selectedId` is
  // the row whose Manage sheet is open: an org id, a member's `user-…` id, or
  // `admin:<orgId>:<user-…>` for an admin row under their org. Kept across a
  // client round trip, so Back lands with the same sheet open.
  const [selectedId, setSelectedId] = useState<string | null>(() => sessionStorage.getItem('tm.selected'));
  const [clientAgentId, setClientAgentId] = useState<string | null>(() => sessionStorage.getItem('tm.agent'));
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('tm.expanded') ?? '[]') as string[]); } catch { return new Set(); }
  });
  React.useEffect(() => { if (selectedId) sessionStorage.setItem('tm.selected', selectedId); else sessionStorage.removeItem('tm.selected'); }, [selectedId]);
  React.useEffect(() => { if (clientAgentId) sessionStorage.setItem('tm.agent', clientAgentId); else sessionStorage.removeItem('tm.agent'); }, [clientAgentId]);
  React.useEffect(() => { sessionStorage.setItem('tm.expanded', JSON.stringify([...expanded])); }, [expanded]);

  // On the standalone page the AppShell top bar hosts Back + breadcrumb (the
  // app-wide convention — same spot as the client view's Back). PageWrapper
  // renders the slot; we portal into it. In the panel there is no AppShell,
  // so navigation renders inline at the top of the tab instead.
  const [headerHost, setHeaderHost] = useState<HTMLElement | null>(null);
  React.useLayoutEffect(() => { setHeaderHost(document.getElementById('tm-header-slot')); }, []);

  // --- Flag overrides: server rows + optimistic local writes layered on top.
  // `null` in the local layer means "reset pending": the server row is being
  // deleted, so the node reads as inherited straight away.
  const [localOverrides, setLocalOverrides] = useState<Record<string, boolean | null>>({});
  const flagOverrides = useMemo(() => {
    const merged: Record<string, boolean> = { ...(data?.overrides ?? {}) };
    for (const [k, v] of Object.entries(localOverrides)) {
      if (v === null) delete merged[k]; else merged[k] = v;
    }
    return merged;
  }, [data?.overrides, localOverrides]);

  const root = data?.root ?? null;

  // Admin rows carry the org they sit under, so the panel and breadcrumb can
  // name it. Parsed from the selection id rather than stored separately.
  const selectedAdmin = useMemo(() => {
    if (!root || !selectedId?.startsWith('admin:')) return null;
    const [, orgId, adminId] = selectedId.split(':');
    const org = findNode(root, orgId);
    const admin = org?.admins?.find(a => a.id === adminId);
    return org && admin ? { org, admin } : null;
  }, [root, selectedId]);
  const selectedNode = useMemo(() => {
    if (!root || selectedAdmin || !selectedId) return null;
    return findNode(root, selectedId);
  }, [root, selectedId, selectedAdmin]);

  // The root is always open, so the first thing on screen is your own level's
  // children rather than a single collapsed row.
  const isExpanded = useCallback((id: string) => id === root?.id || expanded.has(id), [expanded, root]);
  const toggleExpanded = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // A row picked inside a Manage sheet (a child in its list): open every
  // branch above it and bring it into view. Nothing navigates.
  const revealNode = useCallback((id: string) => {
    if (!root) return;
    setExpanded(prev => new Set([...prev, ...ancestorIds(root, id)]));
    requestAnimationFrame(() => document.getElementById(`tm-row-${id}`)?.scrollIntoView({ block: 'center' }));
  }, [root]);

  const expandAll = useCallback(() => {
    if (!root) return;
    const ids = new Set<string>();
    const walk = (n: OrgNode) => {
      if (n.children.length || n.admins?.length) ids.add(n.id);
      n.children.forEach(walk);
    };
    walk(root);
    setExpanded(ids);
  }, [root]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  const toggleFlag = useCallback((nodeId: string, flag: FlagKey) => {
    if (!root) return;
    const node = findNode(root, nodeId);
    if (!node) return;
    const current = hasFlag(root, node, flag, flagOverrides);
    const next = !current;
    const k = nodeId + '__' + flag;
    setLocalOverrides(prev => ({ ...prev, [k]: next }));
    const target = node.type === 'agent' ? node.name : `everyone under ${node.name}`;

    // Sandbox: the cascade is computed in the browser from these overrides, so
    // the local update above IS the real behavior — the tree recalculates
    // exactly as it would live. Skip the RPC (it's swallowed during
    // impersonation, which would silently roll the toggle back) and persist
    // nothing. Everything evaporates on exit.
    if (isSandbox) {
      toast.success(`${next ? 'Enabled' : 'Disabled'} ${flag.toUpperCase()} for ${target} (sandbox)`);
      return;
    }

    setNodeFlag(nodeId, flag, next)
      .then(() => {
        // The switch acts on the node's downline, not the node itself — say so.
        toast.success(`${next ? 'Enabled' : 'Disabled'} ${flag.toUpperCase()} for ${target}`);
        queryClient.invalidateQueries({ queryKey: ['hierarchy-tree'] });
      })
      .catch(err => {
        setLocalOverrides(prev => {
          const copy = { ...prev };
          delete copy[k];
          return copy;
        });
        // setNodeFlag is a raw promise, not a useMutation — the global
        // MutationCache reporter never sees this failure, so report it here.
        void logClientError({
          error_message: `rpc: ${err instanceof Error ? err.message : String(err)}`,
          query_name: nodeId.startsWith('user-') ? 'set_user_flag' : 'set_org_flag',
          error_type: 'rpc',
        });
        toast.error('Flag change failed', { description: sanitizeError(err) });
      });
  }, [root, flagOverrides, queryClient, isSandbox]);

  // Drops the node's own setting so it follows its parent again. Without it,
  // one flip pinned a level forever: deepest explicit setting wins, so a later
  // change higher up never reached it.
  const resetFlag = useCallback((nodeId: string, flag: FlagKey) => {
    if (!root) return;
    const node = findNode(root, nodeId);
    if (!node) return;
    const k = nodeId + '__' + flag;
    const before = localOverrides[k];
    setLocalOverrides(prev => ({ ...prev, [k]: null }));
    if (isSandbox) {
      toast.success(`${flag.toUpperCase()} for ${node.name} now follows the level above (sandbox)`);
      return;
    }
    clearNodeFlag(nodeId, flag)
      .then(() => {
        toast.success(`${flag.toUpperCase()} for ${node.name} now follows the level above`);
        queryClient.invalidateQueries({ queryKey: ['hierarchy-tree'] });
      })
      .catch(err => {
        setLocalOverrides(prev => {
          const copy = { ...prev };
          if (before === undefined) delete copy[k]; else copy[k] = before;
          return copy;
        });
        void logClientError({
          error_message: `rpc: ${err instanceof Error ? err.message : String(err)}`,
          query_name: nodeId.startsWith('user-') ? 'clear_user_flag' : 'clear_org_flag',
          error_type: 'rpc',
        });
        toast.error('Reset failed', { description: sanitizeError(err) });
      });
  }, [root, localOverrides, queryClient, isSandbox]);

  // --- Search --- kept in sessionStorage, so opening a client from the
  // results and coming back lands on the same results.
  const [search, setSearch] = useState(() => sessionStorage.getItem('tm.search') ?? '');
  React.useEffect(() => { if (search) sessionStorage.setItem('tm.search', search); else sessionStorage.removeItem('tm.search'); }, [search]);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  // The level picker works on its own. With the search box empty, IMOs /
  // Agencies / Teams / Agents turn the table into just that level — each row
  // still expands in place, so picking IMOs and opening Test Org stays in the
  // IMO view. Clients (not rows in the table) and any typed search show the
  // results list instead.
  // Picking a row from search results focuses the table on just that row,
  // opened, with a way back to everything.
  const [focusId, setFocusId] = useState<string | null>(null);
  const focusNode = useMemo(() => (root && focusId ? findNode(root, focusId) : null), [root, focusId]);
  const levelRows = useMemo(() => {
    if (!root || search.trim() || typeFilter === 'clients') return null;
    if (typeFilter === 'all') return focusNode ? [focusNode] : null;
    return flattenDownline(root).filter(n => n.type === typeFilter);
  }, [root, search, typeFilter, focusNode]);
  // Home: the whole table again. The bar's name (or logo) is the button.
  const goHome = useCallback(() => {
    setTypeFilter('all');
    setFocusId(null);
    setSearch('');
  }, []);
  const focusOn = useCallback((id: string) => {
    setFocusId(id);
    setExpanded(prev => new Set([...prev, id]));
    setSearch('');
    setTypeFilter('all');
  }, []);
  const filtering = !!search.trim() || typeFilter === 'clients';
  const searchResults = useMemo(() => {
    if (!root || !filtering) return null;
    const q = search.trim().toLowerCase();
    const all = flattenDownline(root);
    return all.filter(n => {
      if (typeFilter !== 'all' && n.type !== typeFilter) return false;
      return !q || n.name.toLowerCase().includes(q) || n.admin.toLowerCase().includes(q);
    });
  }, [root, search, typeFilter, filtering]);

  // Client hits ride the same search box. Clients only render at agent leaves
  // in the tree, so without this you'd have to know which agent holds a client
  // before you could find it.
  const clientResults = useMemo(() => {
    if (!root || !filtering) return null;
    if (typeFilter !== 'all' && typeFilter !== 'clients') return [];
    return search.trim() ? searchClients(root, search) : allClients(root);
  }, [root, search, typeFilter, filtering]);

  // --- Activity log ---
  const [auditOpen, setAuditOpen] = useState(false);

  // --- Invites: every invite at or below you, who sent it and into where.
  // A panel, like the Activity log, so the table never grows with invites.
  const [invitesOpen, setInvitesOpen] = useState(false);
  const [invitesView, setInvitesView] = useState<'open' | 'accepted'>('open');
  const invitesQuery = useDownlineInvites(!!data);
  const [inviteBusyId, setInviteBusyId] = useState<string | null>(null);
  const handleResendInvite = useCallback(async (inv: DownlineInvite) => {
    if (isSandbox) {
      toast.success('Invite resent (sandbox)', { description: 'Nothing was saved or sent.' });
      return;
    }
    setInviteBusyId(inv.id);
    try {
      const emailSent = await resendDownlineInvite(inv.id);
      toast.success(emailSent ? 'Invite resent' : 'Invite renewed', {
        description: emailSent
          ? `Email sent to ${inv.email}. The link is good for another ${INVITE_TTL_DAYS} days.`
          : `The link is good for another ${INVITE_TTL_DAYS} days.`,
      });
      queryClient.invalidateQueries({ queryKey: ['downline-invites'] });
    } catch (err) {
      toast.error('Could not resend invite', { description: sanitizeError(err) });
    } finally {
      setInviteBusyId(null);
    }
  }, [isSandbox, queryClient]);
  const handleRevokeInvite = useCallback(async (inv: DownlineInvite) => {
    if (isSandbox) {
      toast.success('Invite revoked (sandbox)', { description: 'Nothing was saved.' });
      return;
    }
    setInviteBusyId(inv.id);
    try {
      await revokeDownlineInvite(inv.id);
      toast.success('Invite revoked');
      queryClient.invalidateQueries({ queryKey: ['downline-invites'] });
    } catch (err) {
      toast.error('Could not revoke invite', { description: sanitizeError(err) });
    } finally {
      setInviteBusyId(null);
    }
  }, [isSandbox, queryClient]);

  // --- Add modal: creates a real typed invite under the org it was opened from ---
  const [addTarget, setAddTarget] = useState<OrgNode | null>(null);
  const addOpen = !!addTarget;
  const setAddOpen = (open: boolean) => { if (!open) setAddTarget(null); };
  // One allowed kind (a new IMO under the platform) is picked for you.
  const openAdd = (n: OrgNode) => {
    const opts = addOptions(n.type);
    setAddKind(opts.length === 1 ? opts[0].value : '');
    setAddName('');
    setAddTarget(n);
  };
  const [addKind, setAddKind] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);

  const handleAdd = useCallback(async () => {
    if (!addTarget || !addKind || !addEmail.trim()) return;
    if (namedByAdder(addKind) && !addName.trim()) return;
    setAddSubmitting(true);
    try {
      // Any '*-admin' kind (imo-admin/agency-admin/team-admin) is a join-invite
      // at admin level; org kinds (imo/agency/team) create new orgs.
      const kind = addKind.endsWith('-admin') ? 'org_admin' : addKind;

      // Sandbox: run the flow to completion on screen without creating a real
      // invite. The link is deliberately marked so it can't be mistaken for a
      // working one — a fake token would lead nowhere if sent.
      if (isSandbox) {
        toast.success('Invite created (sandbox)', {
          description: `A ${addKind} invite for ${addEmail.trim()} — nothing was saved or sent.`,
          duration: 6000,
        });
        setAddTarget(null);
        setAddEmail('');
        setAddName('');
        setAddKind('');
        return;
      }
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string, args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: Error | null }>)('create_hierarchy_invite', {
        p_parent_org: addTarget.id,
        p_kind: kind,
        p_email: addEmail.trim(),
        // Agency / team only: the adder names the branch (20260928000001).
        p_org_name: namedByAdder(addKind) ? addName.trim() : null,
      });
      if (error) throw error;
      const token = (data as { token?: string } | null)?.token;
      if (!token) throw new Error('No invite token returned');
      const link = `${window.location.origin}/invite/${token}`;
      await navigator.clipboard.writeText(link).catch(() => {});

      // Email the invite link. The invite exists either way — email failure
      // downgrades the toast, it never fails the flow.
      let emailSent = false;
      try {
        const { data: sendData } = await supabase.functions.invoke('send-invite-email', {
          body: { token },
        });
        emailSent = !!(sendData as { emailSent?: boolean } | null)?.emailSent;
      } catch {
        // fall through to the link-only toast
      }

      queryClient.invalidateQueries({ queryKey: ['downline-invites'] });
      if (emailSent) {
        toast.success('Invite sent — link copied', {
          description: `Email sent to ${addEmail.trim()}. The link is on your clipboard too.`,
          duration: 8000,
        });
      } else {
        toast.success('Invite created — link copied', {
          description: `The email could not be sent — share the link with ${addEmail.trim()} directly.`,
          duration: 8000,
        });
      }
      setAddTarget(null);
      setAddEmail('');
      setAddName('');
      setAddKind('');
    } catch (err) {
      toast.error('Could not create invite', { description: sanitizeError(err) });
    } finally {
      setAddSubmitting(false);
    }
  }, [addTarget, addKind, addEmail, addName, isSandbox, queryClient]);

  // --- View as (super admin only): impersonate an agent from their node ---
  const handleViewAs = useCallback((n: OrgNode) => {
    if (refuseSample()) return;
    const uid = nodeUserId(n);
    if (!uid) return;
    startImpersonation(uid).catch(err =>
      toast.error('Could not start impersonation', { description: sanitizeError(err) }));
  }, [startImpersonation, refuseSample]);

  // --- Management actions (rename dialog, delete confirm) ---
  const [renameTarget, setRenameTarget] = useState<{ kind: 'org' | 'user'; id: string; current: string } | null>(null);
  const [renameValue, setRenameValue] = useState('');
  // Downline deactivates, never deletes (Drew, 2026-09-29). A member →
  // deactivate_member: signed out and off every screen now, clients kept with
  // them, hidden; platform staff bring them back from the Admin Panel. Their
  // email is typed first, compared trimmed and case-insensitively so a
  // capitalised stored address can't leave the button dead. An org →
  // BranchActionDialog, which asks "all of it, or let me choose?".
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'user'; id: string; name: string; email?: string } | null>(null);
  const [branchTarget, setBranchTarget] = useState<OrgNode | null>(null);
  const [moveTarget, setMoveTarget] = useState<OrgNode | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  const afterAction = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['hierarchy-tree'] });
  }, [queryClient]);

  // node.id is `user-${uuid}` for agents, the org UUID otherwise.
  const nodeUserId = (n: OrgNode) => n.id.startsWith('user-') ? n.id.slice(5) : null;

  const handleSuspendToggle = useCallback(async (n: OrgNode) => {
    if (refuseSample()) return;
    const uid = nodeUserId(n);
    if (!uid) return;
    const next = n.status === 'suspended' ? 'active' : 'suspended';
    try {
      await setMemberStatus(uid, next);
      toast.success(next === 'suspended' ? `${n.name} suspended` : `${n.name} reactivated`);
      afterAction();
    } catch (err) {
      toast.error('Action failed', { description: sanitizeError(err) });
    }
  }, [afterAction, refuseSample]);

  // Platform staff only: flip whether this person carries a client book.
  // Off = support admin who builds work for the advisors; My Clients hidden,
  // All Clients is home. The RPC refuses anyone who isn't platform staff.
  const handleClientBookToggle = useCallback(async (n: OrgNode) => {
    if (refuseSample()) return;
    const uid = nodeUserId(n);
    if (!uid) return;
    const next = n.hasClientBook === false;
    try {
      await setMemberClientBook(uid, next);
      toast.success(next ? `${n.name} has their own clients again` : `${n.name} set to no own clients`);
      afterAction();
    } catch (err) {
      toast.error('Action failed', { description: sanitizeError(err) });
    }
  }, [afterAction, refuseSample]);

  // Promote an agent to admin, or put an admin back to agent, inside your own
  // downline. Nothing happens to their clients: a client is owned by user_id,
  // so the person keeps their whole book across the change (Drew, 2026-09-22 -
  // the old workaround was delete + re-invite, which handed the book to the
  // org owner on the way out). Their new scope comes from where they already
  // sit: get_hierarchy_tree roots a non-platform caller at their own org, so an
  // agent promoted inside an Agency node manages that Agency and below.
  const handleRoleChange = useCallback(async (n: OrgNode, role: 'agent' | 'org_admin') => {
    if (refuseSample()) return;
    const uid = nodeUserId(n);
    if (!uid) return;
    try {
      await setMemberRole(uid, role);
      toast.success(role === 'org_admin' ? `${n.name} is now an admin` : `${n.name} is now an agent`, {
        description: 'Their clients are unchanged.',
      });
      afterAction();
    } catch (err) {
      toast.error('Could not change role', { description: sanitizeError(err) });
    }
  }, [afterAction, refuseSample]);

  const handleResetPassword = useCallback(async (n: OrgNode) => {
    if (refuseSample()) return;
    if (!n.email) { toast.error('No email on file for this user'); return; }
    try {
      await sendPasswordReset(n.email);
      toast.success('Password reset link sent', { description: `Sent to ${n.email}.` });
    } catch (err) {
      toast.error('Could not send reset', { description: sanitizeError(err) });
    }
  }, [refuseSample]);

  const openRename = useCallback((n: OrgNode) => {
    if (refuseSample()) return;
    const uid = nodeUserId(n);
    setRenameTarget({ kind: uid ? 'user' : 'org', id: uid ?? n.id, current: n.name });
    setRenameValue(n.name);
  }, [refuseSample]);

  const submitRename = useCallback(async () => {
    if (!renameTarget || !renameValue.trim()) return;
    setActionBusy(true);
    try {
      if (renameTarget.kind === 'org') await renameOrg(renameTarget.id, renameValue.trim());
      else await renameMember(renameTarget.id, renameValue.trim());
      toast.success('Renamed');
      setRenameTarget(null);
      afterAction();
    } catch (err) {
      toast.error('Rename failed', { description: sanitizeError(err) });
    } finally {
      setActionBusy(false);
    }
  }, [renameTarget, renameValue, afterAction]);

  const openDelete = useCallback((n: OrgNode) => {
    if (refuseSample()) return;
    const uid = nodeUserId(n);
    setDeleteConfirm('');
    if (uid) setDeleteTarget({ kind: 'user', id: uid, name: n.name, email: n.email });
    else setBranchTarget(n);
  }, [refuseSample]);

  const deleteConfirmOk = !!deleteTarget
    && deleteConfirm.trim().toLowerCase() === (deleteTarget.email ?? deleteTarget.name).trim().toLowerCase();

  const submitDelete = useCallback(async () => {
    if (!deleteTarget || !deleteConfirmOk) return;
    setActionBusy(true);
    try {
      await deactivateMember(deleteTarget.id);
      toast.success(`${deleteTarget.name} deactivated`, {
        description: 'Signed out and off every screen. Their clients stay with them. Bring them back from the Admin Panel.',
      });
      setDeleteTarget(null);
      setDeleteConfirm('');
      setSelectedId(null);
      afterAction();
    } catch (err) {
      toast.error('Deactivate failed', { description: sanitizeError(err) });
    } finally {
      setActionBusy(false);
    }
  }, [deleteTarget, deleteConfirmOk, afterAction]);

  // --- Open a client in the real app (read-only view via in-memory store) ---
  const [openingClientId, setOpeningClientId] = useState<string | null>(null);
  // Super admin never leaves the panel — a clicked client renders as an
  // inline read-only summary right here. Full working views of a client are
  // reached the correct way: View as the agent. Org owners/admins on the
  // standalone page keep the regular-app client view, which IS their world.
  const [panelClient, setPanelClient] = useState<{ name: string; scenarios: TaxScenario[] } | null>(null);

  const openClient = useCallback(async (client: ClientRef) => {
    if (openingClientId) return;
    setOpeningClientId(client.id);
    try {
      const scenarios = await fetchClientScenarios(client.id);
      if (scenarios.length === 0) {
        toast.error('No scenarios found for this client');
        return;
      }
      if (isSuperAdmin && !isImpersonating) {
        setPanelClient({ name: client.name, scenarios });
        return;
      }
      navigate('/', { state: { demoScenarios: scenarios, demoClientId: scenarios[0].clientId } });
    } catch (err) {
      toast.error('Could not open client', { description: sanitizeError(err) });
    } finally {
      setOpeningClientId(null);
    }
  }, [navigate, openingClientId, isSuperAdmin, isImpersonating]);

  // --- Loading / error states ---
  if (isLoading) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading your network…</p>
        </div>
      </>
    );
  }
  if (error || !root || !data) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-center max-w-md mx-auto">
          <p className="text-sm font-semibold text-foreground">Couldn't load your network</p>
          <p className="text-xs text-muted-foreground">{error ? sanitizeError(error) : 'No hierarchy data returned.'}</p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>Try again</Button>
        </div>
      </>
    );
  }

  // --- Navigation header for the two screens beyond the table ---
  // An agent's client grid, and a client open inline (super admin). The
  // breadcrumb names the path to the agent; Back pops one step.
  const agentNode = clientAgentId ? findNode(root, clientAgentId) : null;
  const goBackOneLevel = () => {
    if (panelClient) { setPanelClient(null); return; }
    if (clientAgentId) { setClientAgentId(null); return; }
  };
  const crumbAgent = agentNode ?? (panelClient ? selectedNode : null);
  const crumbs: { key: string; name: string; onClick: () => void }[] = [
    { key: 'table', name: 'Downline', onClick: () => { setPanelClient(null); setClientAgentId(null); } },
    ...(crumbAgent ? (findPath(root, crumbAgent.id) ?? []).slice(1).map(n => ({
      key: n.id, name: n.name, onClick: () => { if (n.id === crumbAgent.id) setPanelClient(null); },
    })) : []),
    ...(panelClient ? [{ key: 'client', name: panelClient.name, onClick: () => {} }] : []),
  ];
  const showBack = !!panelClient || !!clientAgentId;
  const navRow = (
    <>
      {showBack && (
        <button
          onClick={goBackOneLevel}
          className="inline-flex items-center gap-1 mr-2 px-2 py-1 rounded-md text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-accent/50 border shrink-0"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Back
        </button>
      )}
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <React.Fragment key={c.key}>
            <button
              onClick={c.onClick}
              className={`px-2 py-0.5 rounded-md text-xs truncate max-w-[180px] ${isLast ? 'font-bold text-foreground bg-muted' : 'font-semibold text-muted-foreground hover:text-foreground'}`}
            >
              {c.name}
            </button>
            {!isLast && <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0" />}
          </React.Fragment>
        );
      })}
    </>
  );
  // Same navigation, one physical spot: the top bar on the standalone page
  // (portaled into AppShell's header slot), inline at the top of the tab in
  // the panel, where the panel's own header is the chrome.
  const navHeader = headerHost
    ? createPortal(<div className="flex items-center gap-1 min-w-0 overflow-hidden">{navRow}</div>, headerHost)
    : <div className="flex items-center gap-1 flex-wrap text-sm mb-4 mt-4">{navRow}</div>;

  // --- Inline client view (panel context) — read-only, never leaves here ---
  if (panelClient) {
    const usd = (n: number | undefined) => n ? '$' + Math.round(n).toLocaleString() : '—';
    const kUsd = (n: number | undefined) => n ? '$' + Math.round(n / 1000) + 'K' : '—';
    // get_client_scenarios returns EVERY version in the client folder, current
    // (root) version first. Ungrouped they render as duplicate-looking rows —
    // group them by version id so the folder's history reads as history.
    const versionGroups: { clientId: string; scenarios: TaxScenario[] }[] = [];
    for (const s of panelClient.scenarios) {
      const last = versionGroups[versionGroups.length - 1];
      if (last && last.clientId === s.clientId) last.scenarios.push(s);
      else versionGroups.push({ clientId: s.clientId, scenarios: [s] });
    }
    return (
      <>
        {navHeader}
        <Card className="shadow-sm">
          <CardContent className="px-4 py-3.5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-primary" />
                <h2 className="text-[15px] font-bold text-foreground">{panelClient.name}</h2>
                <span className="text-[10px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Read only</span>
              </div>
              <p className="text-[11px] text-muted-foreground">Use “View as” on the agent to see this client exactly as they do.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground border-b">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4 font-mono">Roth</th>
                    <th className="py-2 pr-4 font-mono">Alt</th>
                    <th className="py-2 pr-4 font-mono">AGI</th>
                    <th className="py-2 pr-4 font-mono">Total Tax</th>
                    <th className="py-2 font-mono">Eff. Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {versionGroups.map((g, gi) => (
                    <React.Fragment key={g.clientId}>
                      {versionGroups.length > 1 && (
                        <tr className="bg-muted/40">
                          <td colSpan={6} className="py-1.5 pr-4 text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground">
                            {gi === 0 ? 'Current version' : `Earlier version ${gi}`}
                          </td>
                        </tr>
                      )}
                      {g.scenarios.map(s => (
                        <tr key={s.id} className="border-b last:border-b-0">
                          <td className="py-2.5 pr-4 font-semibold text-foreground">{s.input.name}</td>
                          <td className="py-2.5 pr-4 font-mono">{kUsd(s.input.rothConversionAmount)}</td>
                          <td className="py-2.5 pr-4 font-mono">{kUsd(s.input.alternativeInvestmentAmount)}</td>
                          <td className="py-2.5 pr-4 font-mono">{usd(s.results?.adjustedGrossIncome)}</td>
                          <td className="py-2.5 pr-4 font-mono text-primary">{usd(s.results?.totalTaxLiability)}</td>
                          <td className="py-2.5 font-mono">{s.results?.effectiveTaxRate != null ? (s.results.effectiveTaxRate * 100).toFixed(2) + '%' : '—'}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  // --- Agent client list view ---
  if (clientAgentId) {
    const agent = findNode(root, clientAgentId);
    if (!agent) { setClientAgentId(null); return null; }
    return (
      <>
        {navHeader}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-sm font-bold text-[#2D4B6E] shrink-0">
              {initials(agent.name)}
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">{agent.name}</h2>
              <p className="text-xs text-muted-foreground">
                {(() => { const p = parentOf(root, agent.id); return p ? p.name : ''; })()}
              </p>
            </div>
          </div>
          <p className="text-sm font-semibold text-foreground/80 mt-4 mb-3">{agent.clientCount} clients</p>
          {agent.clients.length === 0 ? (
            <div className="border-2 border-dashed border-border rounded-lg py-10 text-center text-sm text-muted-foreground">
              No clients yet.
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(186px,1fr))] gap-5">
              {agent.clients.map(cl => (
                <motion.div
                  key={cl.id}
                  whileHover={{ y: -3 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                  className="group relative"
                >
                  <div
                    className="relative rounded-lg bg-card border border-border shadow-sm group-hover:shadow-md group-hover:border-primary/40 transition-all cursor-pointer overflow-visible"
                    style={{ minHeight: 120 }}
                    onClick={() => openClient(cl)}
                  >
                    <div className="absolute -top-[6px] left-3 h-2 w-10 rounded-t-md bg-primary/15 border border-primary/30 border-b-0" />
                    <div className="p-5 pt-[18px] pb-4 flex flex-col min-h-[inherit]">
                      <div className="flex items-center gap-2 mb-1.5">
                        {openingClientId === cl.id
                          ? <Loader2 className="h-[19px] w-[19px] text-primary shrink-0 animate-spin" />
                          : <FolderOpen className="h-[19px] w-[19px] text-primary shrink-0" />}
                        <h3 className="text-[13.5px] font-semibold text-foreground truncate">{cl.name}</h3>
                      </div>
                      <p className="mt-auto text-xs text-muted-foreground">Edited {cl.lastEdit}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  }

  // --- Main navigator ---
  return (
    <>
      {/* Header — title and purpose on one line, mock-style */}
      <div className="flex items-center justify-between gap-x-4 gap-y-2 mb-3 flex-wrap">
        <div className="flex items-baseline gap-2.5 flex-wrap min-w-0">
          <h1 className="text-[17px] font-bold tracking-tight text-foreground" data-tour="downline-title">Downline Management</h1>
          <p className="text-xs text-muted-foreground truncate">
            {sampleActive ? 'A sample downline, while the walkthrough plays.' : data.isPlatformView
              ? 'All IMOs, agencies, teams, and agents. Feature access cascades down.'
              : 'Your agencies, teams, and agents. Feature access cascades down.'}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 h-8 text-xs"
            onClick={() => navigate('/', { state: { initialView: 'org-scenarios' } })}
            data-tour="downline-all-clients"
          >
            <FolderOpen className="h-3.5 w-3.5" /> All clients
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 h-8 text-xs"
            onClick={() => setAuditOpen(true)}
            data-tour="downline-activity"
          >
            <ScrollText className="h-3.5 w-3.5" /> Activity log
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 h-8 text-xs"
            onClick={() => setInvitesOpen(true)}
            data-tour="downline-invites"
          >
            <Mail className="h-3.5 w-3.5" /> Invites
            {countPending(invitesQuery.data ?? []) > 0 && (
              <span className="ml-0.5 rounded-full bg-primary text-white text-[10px] font-semibold px-1.5 leading-4">
                {countPending(invitesQuery.data ?? [])}
              </span>
            )}
          </Button>
        </div>
      </div>

      {sampleActive && (
        <div
          data-tour="downline-sample"
          className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm dark:border-amber-800 dark:bg-amber-950/30"
        >
          <strong>Sample downline</strong> — {SAMPLE_NOTICE}
        </div>
      )}

      {/* Search + filter + invite */}
      <div className="flex items-center gap-2.5 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-[460px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search your downline — agencies, teams, agents, clients…"
            className="pl-8 h-8 text-xs"
            data-tour="downline-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setFocusId(null); }}>
          <SelectTrigger className="w-[130px] h-8 text-xs" data-tour="downline-level">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All levels</SelectItem>
            <SelectItem value="imo">IMOs</SelectItem>
            <SelectItem value="agency">Agencies</SelectItem>
            <SelectItem value="team">Teams</SelectItem>
            <SelectItem value="agent">Agents</SelectItem>
            <SelectItem value="clients">Clients</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Search results */}
      {searchResults ? (
        <Card className="shadow-sm">
          <div className="px-4 py-3 border-b text-xs font-semibold text-muted-foreground tracking-wide">
            {(() => {
              const n = searchResults.length + (clientResults?.length ?? 0);
              if (search.trim()) return `${n} result${n !== 1 ? 's' : ''} for "${search}"`;
              const [one, many] = LEVEL_WORDS[typeFilter] ?? ['result', 'results'];
              return `${n} ${n === 1 ? one : many}`;
            })()}
          </div>
          {searchResults.length === 0 && (clientResults?.length ?? 0) === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {search.trim() ? 'No matches in your downline.' : `No ${(LEVEL_WORDS[typeFilter] ?? ['', 'matches'])[1]} in your downline.`}
            </div>
          ) : (
            searchResults.map(sr => {
              const t = TILE[sr.type];
              const path = findPath(root, sr.id);
              const crumb = path ? path.slice(0, -1).filter(n => n.type !== 'platform').map(n => n.name).join(' › ') : '';
              return (
                <div
                  key={sr.id}
                  onClick={() => focusOn(sr.id)}
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-accent/50 border-b last:border-b-0"
                >
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${t.bg} ${t.text}`}>
                    {React.cloneElement(t.icon as React.ReactElement, { className: 'h-4 w-4' })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground truncate">{sr.name}</span>
                      <span className={`text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded ${TYPE_BADGE[sr.type].bg} ${TYPE_BADGE[sr.type].text}`}>
                        {typeLabel(sr.type)}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">{crumb}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
                </div>
              );
            }).concat(
              (clientResults ?? []).map(hit => (
                <ClientRow
                  key={`client-${hit.client.id}`}
                  hit={hit}
                  root={root}
                  busy={openingClientId === hit.client.id}
                  // Deliberately does NOT clear the search: opening a client is
                  // async, so clearing first unmounts these results and flashes
                  // the tree for the length of the fetch. The row spins in place
                  // instead. The search is kept in sessionStorage, so coming
                  // back from the client — inline Back for a super admin, the
                  // browser's Back for everyone else — lands on these results.
                  onOpen={() => openClient(hit.client)}
                />
              ))
            )
          )}
        </Card>
      ) : (
        // The whole downline in one expandable table. Org rows expand and
        // collapse; Manage opens one sheet with
        // everything for that row. Nothing on this screen navigates away.
        <Card className="shadow-sm overflow-hidden">
          {/* One strip above the column headings: the top of YOUR tree, as a bar
              rather than a row. Tactical Tax's shows the logo; every other
              owner's shows their org's name. Clicking it is home — it clears
              the level filter, a focused row and the search in one go. Beside
              it: the switches for everything under you, the one add button,
              and Expand / Collapse all. */}
          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2 border-b">
            <button
              type="button"
              onClick={goHome}
              title="Show everything"
              data-tour="downline-home"
              className="flex items-center gap-2.5 min-w-0 rounded-md -mx-1 px-1 py-0.5 hover:bg-accent/40"
            >
              {root.type === 'platform' && <img src={tacticalLogo} alt="" className="h-7 w-7 shrink-0 object-contain" />}
              <span className="text-[15px] font-bold text-foreground truncate">
                {root.type === 'platform' ? 'Tactical Tax Genie' : root.name}
              </span>
            </button>
            <div className="flex items-center gap-2 flex-wrap" data-tour="downline-flags">
              {FLAG_META.filter(f => data.granted[f.key]).map(f => {
                const isOn = hasFlag(root, root, f.key, flagOverrides);
                const scope = root.type === 'platform' ? 'all IMOs' : 'your downline';
                return (
                  <label key={f.key} className={`flex items-center gap-2 rounded-md border px-2.5 py-1 cursor-pointer ${isOn ? 'bg-primary/5 border-primary/20' : 'bg-card border-border'}`}>
                    <Switch checked={isOn} onCheckedChange={() => toggleFlag(root.id, f.key)} aria-label={`${f.label} for ${scope}`} className="scale-[0.8] -mx-0.5" />
                    <span className="leading-tight">
                      <span className={`block text-xs font-semibold ${isOn ? 'text-primary' : 'text-foreground'}`}>{f.label}</span>
                      <span className="block text-[10px] text-muted-foreground">{isOn ? 'On' : 'Off'} for {scope}</span>
                    </span>
                  </label>
                );
              })}
              {addOptions(root.type).length > 0 && (
                <Button size="sm" className="gap-1 h-8 text-xs shadow-sm" onClick={() => openAdd(root)} data-tour="downline-add">
                  <Plus className="h-3.5 w-3.5" /> {root.type === 'platform' ? 'New IMO' : 'Add'}
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={expandAll} data-tour="downline-expand">Expand all</Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px]" onClick={collapseAll}>Collapse all</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table role="treegrid" aria-label="Downline hierarchy" className="w-full text-[13px]" data-tour="downline-tree">
              <thead>
                <tr className="text-left text-[10.5px] font-semibold tracking-wider uppercase text-muted-foreground border-b">
                  <th className="py-2 pl-4 pr-3">Name</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3 text-right">Members</th>
                  <th className="py-2 pr-3 text-right">Clients</th>
                  <th className="py-2 pr-3">Strategies</th>
                  <th className="py-2 pr-4 text-right"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {levelRows ? (
                  levelRows.length === 0 ? (
                    <tr><td colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                      No {(LEVEL_WORDS[typeFilter] ?? ['', 'matches'])[1]} in your downline.
                    </td></tr>
                  ) : levelRows.map(n => (
                    <TreeTableRows
                      key={n.id}
                      root={root}
                      node={n}
                      parentType={parentOf(root, n.id)?.type}
                      depth={0}
                      showPath
                      isExpanded={isExpanded}
                      onToggle={toggleExpanded}
                      onManage={setSelectedId}
                      granted={data.granted}
                      flagOverrides={flagOverrides}
                    />
                  ))
                ) : (
                <TreeTableRows
                  root={root}
                  node={root}
                  depth={0}
                  skipRow
                  isExpanded={isExpanded}
                  onToggle={toggleExpanded}
                  onManage={setSelectedId}
                  granted={data.granted}
                  flagOverrides={flagOverrides}
                />
                )}
              </tbody>
            </table>
          </div>
          {focusNode && typeFilter === 'all' ? (
            <div className="px-4 py-2 border-t text-[11.5px] text-muted-foreground">
              Showing {focusNode.name}
            </div>
          ) : levelRows ? (
            <div className="px-4 py-2 border-t text-[11.5px] text-muted-foreground">
              {levelRows.length} {levelRows.length === 1 ? LEVEL_WORDS[typeFilter][0] : LEVEL_WORDS[typeFilter][1]}
            </div>
          ) : (() => {
            const r = rollup(root);
            const orgs = r.imo + r.agency + r.team;
            const people = memberCount(root);
            return (
              <div className="px-4 py-2 border-t text-[11.5px] text-muted-foreground">
                {people} member{people === 1 ? '' : 's'} across {orgs} downline organization{orgs === 1 ? '' : 's'} · {r.clients.toLocaleString()} clients
              </div>
            );
          })()}
        </Card>
      )}

      {/* The one Manage sheet — every row's settings, people and flags open here. */}
      <Sheet open={!!(selectedNode || selectedAdmin)} onOpenChange={open => { if (!open) setSelectedId(null); }}>
        <SheetContent
          side="right"
          className="w-[520px] max-w-[94vw] p-0 overflow-y-auto"
          data-tour="downline-sheet"
          // A walkthrough popover sits outside the sheet; a click on it must not
          // count as a click outside and close the sheet mid-tour.
          onPointerDownOutside={e => { if (isTourPopover(e.target)) e.preventDefault(); }}
          onInteractOutside={e => { if (isTourPopover(e.target)) e.preventDefault(); }}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{selectedAdmin ? selectedAdmin.admin.name : selectedNode?.name ?? 'Manage'}</SheetTitle>
          </SheetHeader>
          {selectedAdmin ? (
            <div className="px-5 py-4">
              <p className="text-[11.5px] font-semibold tracking-wide uppercase text-muted-foreground">
                {typeLabel(selectedAdmin.org.type)} Admin · {selectedAdmin.org.name}
              </p>
              <AdminsList admins={[selectedAdmin.admin]} onSuspendToggle={handleSuspendToggle}
                onResetPassword={handleResetPassword} onRename={openRename} onDelete={openDelete} />
            </div>
          ) : selectedNode && (
            <DetailPanel
              root={root}
              node={selectedNode}
              isRoot={selectedNode.id === root.id}
              granted={data.granted}
              flagOverrides={flagOverrides}
              onToggleFlag={toggleFlag}
              onResetFlag={resetFlag}
              onSelect={id => { setSelectedId(id); revealNode(id); }}
              onOpenClients={() => { if (!refuseSample()) setClientAgentId(selectedNode.id); }}
              onAdd={() => openAdd(selectedNode)}
              // Stay in this sheet while the client loads. The open sheet is
              // what sessionStorage restores, so Back returns to exactly here.
              onOpenClient={cl => { void openClient(cl); }}
              openingClientId={openingClientId}
              onViewAs={isSuperAdmin ? handleViewAs : undefined}
              onClientBookToggle={isSuperAdmin || isPlatformAdmin || isOrgOwner ? handleClientBookToggle : undefined}
              onRoleChange={isSuperAdmin || isPlatformAdmin || isOrgOwner ? handleRoleChange : undefined}
              onSuspendToggle={handleSuspendToggle}
              onResetPassword={handleResetPassword}
              onRename={openRename}
              onDelete={openDelete}
              onMove={n => { if (!refuseSample()) setMoveTarget(n); }}
              viewerIsPlatformStaff={isSuperAdmin || isPlatformAdmin}
              viewerIsSuperAdmin={isSuperAdmin}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={o => { if (!o) setRenameTarget(null); }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{renameTarget?.kind === 'user' ? 'Edit name' : 'Rename organization'}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold mb-1.5 block">
              {renameTarget?.kind === 'user' ? 'Full name' : 'Organization name'}
            </Label>
            <Input
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitRename(); }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button onClick={submitRename} disabled={!renameValue.trim() || actionBusy}>
              {actionBusy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate a member. Orgs go through BranchActionDialog below. */}
      <Dialog open={!!deleteTarget} onOpenChange={o => { if (!o) { setDeleteTarget(null); setDeleteConfirm(''); } }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Deactivate {deleteTarget?.name}?</DialogTitle>
            <DialogDescription>
              They're signed out and gone from every screen right away. Their clients stay with them, out of sight. Platform staff can bring them back from the Admin Panel.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-2">
              <Label htmlFor="tm-delete-confirm">Type {deleteTarget.email ? 'their email' : 'their name'} to confirm</Label>
              <Input
                id="tm-delete-confirm"
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                placeholder={deleteTarget.email ?? deleteTarget.name}
                className="font-mono text-sm"
                autoComplete="off"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirm(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={submitDelete} disabled={actionBusy || !deleteConfirmOk}>
              {actionBusy ? 'Deactivating…' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {root && (
        <>
          <BranchActionDialog mode="deactivate" root={root} org={branchTarget} onClose={() => setBranchTarget(null)}
            onDone={() => { setSelectedId(null); afterAction(); }} />
          <MoveDialog root={root} node={moveTarget} onClose={() => setMoveTarget(null)} onDone={afterAction} />
        </>
      )}

      {/* Activity log slide-over */}
      <Sheet open={invitesOpen} onOpenChange={setInvitesOpen}>
        <SheetContent side="right" className="w-[480px] max-w-[94vw] p-0 flex flex-col">
          <SheetHeader className="px-5 py-4 border-b">
            <SheetTitle>Invites</SheetTitle>
            <p className="text-xs text-muted-foreground">Everyone invited anywhere in your downline</p>
            {(() => {
              const { open, accepted } = partitionInvites(invitesQuery.data ?? []);
              return (
                <div className="flex gap-1 pt-2" role="tablist" aria-label="Invite status">
                  <Button size="sm" variant={invitesView === 'open' ? 'default' : 'ghost'} className="h-7 text-xs"
                    role="tab" aria-selected={invitesView === 'open'} onClick={() => setInvitesView('open')}>
                    Pending ({open.length})
                  </Button>
                  <Button size="sm" variant={invitesView === 'accepted' ? 'default' : 'ghost'} className="h-7 text-xs"
                    role="tab" aria-selected={invitesView === 'accepted'} onClick={() => setInvitesView('accepted')}>
                    Accepted ({accepted.length})
                  </Button>
                </div>
              );
            })()}
          </SheetHeader>
          <div className="overflow-y-auto flex-1 px-3 py-1">
            {invitesQuery.isLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : invitesQuery.error ? (
              <p className="text-sm text-muted-foreground text-center py-10">Couldn't load invites. {sanitizeError(invitesQuery.error)}</p>
            ) : (() => {
              const { open, accepted } = partitionInvites(invitesQuery.data ?? []);
              const list = invitesView === 'open' ? open : accepted;
              if (list.length === 0) {
                return <p className="text-sm text-muted-foreground text-center py-10">{invitesView === 'open' ? 'No pending invites.' : 'No accepted invites yet.'}</p>;
              }
              return list.map(inv => {
                const status = inviteStatus(inv);
                // Where it lands in the tree, e.g. "Summit Wealth › Agency West".
                const path = (findPath(root, inv.org_id) ?? [])
                  .filter(n => n.type !== 'platform')
                  .map(n => n.name).join(' › ');
                const busy = inviteBusyId === inv.id;
                return (
                  <div key={inv.id} className="px-2 py-3 border-b last:border-b-0">
                    <p className="text-sm text-foreground leading-snug">
                      <strong className="font-semibold">{inv.email || 'Open invite'}</strong>
                      <span className="text-muted-foreground">: {inviteKindLabel(inv)}, into </span>
                      <strong className="font-semibold">{inv.org_name}</strong>
                      {path && path !== inv.org_name && <span className="text-muted-foreground"> ({path})</span>}
                    </p>
                    <p className="text-[11.5px] text-muted-foreground mt-0.5">
                      Invited by {inv.invited_by_name ?? 'someone no longer here'} · sent {formatDistanceToNow(new Date(inv.created_at), { addSuffix: true })}
                      {status === 'accepted'
                        ? <> · accepted {inv.redeemed_by_name ? `by ${inv.redeemed_by_name} ` : ''}{formatDistanceToNow(new Date(inv.redeemed_at as string), { addSuffix: true })}</>
                        : status === 'expired'
                          ? <> · <span className="text-destructive font-semibold">expired</span></>
                          : <> · expires {formatDistanceToNow(new Date(inv.expires_at), { addSuffix: true })}</>}
                    </p>
                    {status !== 'accepted' && (
                      <div className="flex gap-1.5 mt-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" disabled={busy} onClick={() => handleResendInvite(inv)}>
                          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Resend
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-destructive/40 text-destructive hover:bg-destructive/10" disabled={busy} onClick={() => handleRevokeInvite(inv)}>
                          <Trash2 className="h-3 w-3" /> Revoke
                        </Button>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={auditOpen} onOpenChange={setAuditOpen}>
        <SheetContent side="right" className="w-[420px] max-w-[92vw] p-0">
          <SheetHeader className="px-5 py-4 border-b">
            <SheetTitle>Activity log</SheetTitle>
            <p className="text-xs text-muted-foreground">Scoped to your downline</p>
          </SheetHeader>
          <div className="overflow-y-auto flex-1 px-3 py-2">
            {data.audit.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">No activity yet.</p>
            ) : (
              data.audit.map((a, i) => (
                <div key={i} className="flex gap-3 px-2 py-3 border-b last:border-b-0">
                  <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${AUDIT_DOT[a.kind] ?? 'bg-muted-foreground'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground leading-snug">
                      <strong className="font-semibold">{a.actor}</strong> {a.verb} <strong className="font-semibold">{a.target}</strong>
                    </p>
                    <p className="text-[11.5px] text-muted-foreground mt-0.5">{a.detail ? `${a.detail} · ` : ''}{a.time}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Add modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{addTarget?.type === 'platform' ? 'New IMO' : `Add to ${addTarget?.name}`}</DialogTitle>
            <DialogDescription>
              {addTarget?.type === 'platform'
                ? "Invite the IMO's owner. They name their organization at signup and it becomes a partner IMO under you."
                : `Everything you add here sits inside ${addTarget?.name}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {addOptions(addTarget?.type ?? 'agent').length > 1 && (
            <div>
              <Label className="text-xs font-semibold mb-2 block">What are you adding?</Label>
              <div className="flex gap-2 flex-wrap">
                {addOptions(addTarget?.type ?? 'agent').map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setAddKind(opt.value)}
                    className={`flex-1 min-w-[80px] h-9 rounded-md border text-sm font-semibold transition-colors ${
                      addKind === opt.value
                        ? 'bg-primary text-white border-primary'
                        : 'bg-card text-foreground border-border hover:bg-accent/50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            )}
            {namedByAdder(addKind) && (
            <div>
              <Label htmlFor="add-name" className="text-xs font-semibold mb-1.5 block">{addKind === 'agency' ? 'Agency name' : 'Team name'}</Label>
              <Input
                id="add-name"
                value={addName}
                onChange={e => setAddName(e.target.value)}
                maxLength={120}
                placeholder={addKind === 'agency' ? 'Dallas Agency' : 'Smith Team'}
              />
            </div>
            )}
            <div>
              <Label className="text-xs font-semibold mb-1.5 block">{addTarget?.type === 'platform' ? "IMO owner's email" : namedByAdder(addKind) ? 'Email of the person who runs it' : 'Email'}</Label>
              <Input
                type="email"
                value={addEmail}
                onChange={e => setAddEmail(e.target.value)}
                placeholder="them@example.com"
              />
              {addHelp(addKind, addTarget?.name ?? '') && (
                <p className="text-xs text-muted-foreground mt-1.5">{addHelp(addKind, addTarget?.name ?? '')}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={!addKind || !addEmail.trim() || (namedByAdder(addKind) && !addName.trim()) || addSubmitting}>
              {addSubmitting ? 'Creating…' : 'Create Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ===========================================================================
// SUB-COMPONENTS
// ===========================================================================


/**
 * An org's admins, with the same controls an agent tile carries. The tree
 * only renders people who have clients, so an admin with no book used to be
 * invisible — an owner whose admin left had no way to suspend or remove them,
 * and that person kept read access to the whole downline. Lives in a drawer
 * opened from an "Admins" button, so ten admins never stretch the page.
 */
function AdminsList({
  admins, onSuspendToggle, onResetPassword, onRename, onDelete,
}: {
  admins: OrgNode[];
  onSuspendToggle: (n: OrgNode) => void;
  onResetPassword: (n: OrgNode) => void;
  onRename: (n: OrgNode) => void;
  onDelete: (n: OrgNode) => void;
}) {
  if (admins.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-10">No admins.</p>;
  }
  return (
    <div className="divide-y">
      {admins.map(a => {
        const suspended = a.status === 'suspended';
        return (
          <div key={a.id} className="px-2 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-[30px] w-[30px] rounded-full bg-muted flex items-center justify-center shrink-0 text-[11px] font-bold text-[#2D4B6E]">
                {initials(a.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-foreground truncate">{a.name}</p>
                  {suspended && <Badge variant="destructive" className="text-[9.5px] px-1.5 py-0">Suspended</Badge>}
                  {a.status === 'pending' && <Badge variant="outline" className="text-[9.5px] px-1.5 py-0 border-amber-300 text-amber-800">Pending</Badge>}
                </div>
                {a.email && a.email !== a.name && <p className="text-[11.5px] text-muted-foreground truncate">{a.email}</p>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-2 pl-[42px]">
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onResetPassword(a)}>
                <KeyRound className="h-3.5 w-3.5" /> Reset password
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onRename(a)}>
                <Pencil className="h-3.5 w-3.5" /> Edit name
              </Button>
              <Button size="sm" variant="outline"
                className={`h-7 text-xs gap-1 ${suspended ? 'border-emerald-400 text-emerald-700 hover:bg-emerald-50' : 'border-destructive/40 text-destructive hover:bg-destructive/10'}`}
                onClick={() => onSuspendToggle(a)}>
                {suspended ? 'Reactivate' : 'Suspend'}
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => onDelete(a)}>
                <UserX className="h-3.5 w-3.5" /> Deactivate
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** What a member row calls the person: their real role, not the leaf type.
 *  An admin reads "IMO Admin" / "Agency Admin" like the admins-only rows. */
function memberLabel(n: OrgNode, parentType?: OrgNodeType): string {
  if (n.orgRole === 'org_owner') return 'Owner';
  if (n.orgRole === 'org_admin') return parentType ? `${typeLabel(parentType)} Admin` : 'Admin';
  return 'Agent';
}

/**
 * One node of the downline as table rows, then its admins and children when
 * expanded. A row click only ever expands or collapses an org; Manage is the
 * one way into the sheet, on every row (Drew, 2026-09-24: a person's row
 * opening the sheet made a row click mean two things). Admins sit in their org as ordinary rows with an Admin type — one
 * with no client book has no other place in the tree.
 *
 * Strategies shows each flag that is on at that row. A solid badge means the
 * level set it itself; an outlined one means it is inherited. A flag set OFF
 * at a level shows struck through there, which is where the cascade changes.
 */
function TreeTableRows({
  root, node, parentType, depth, skipRow = false, showPath = false, isExpanded, onToggle, onManage, granted, flagOverrides,
}: {
  root: OrgNode;
  node: OrgNode;
  /** Level-filter rows: name where the row sits, since its parents aren't shown. */
  showPath?: boolean;
  /** Render only what sits under this node, at this depth. The top of the
   *  viewer's tree is a bar above the table, not a row in it. */
  skipRow?: boolean;
  /** The org this row sits in — names an admin's level. */
  parentType?: OrgNodeType;
  depth: number;
  isExpanded: (id: string) => boolean;
  onToggle: (id: string) => void;
  onManage: (id: string) => void;
  granted: Record<FlagKey, boolean>;
  flagOverrides: Record<string, boolean>;
}) {
  const isPerson = node.type === 'agent';
  // An admin who carries clients already has a person row among the children
  // (that is how their clients show); only clientless admins need a row here.
  const admins = isPerson ? [] : (node.admins ?? []).filter(a => !node.children.some(c => c.id === a.id));
  const canExpand = !isPerson && (node.children.length > 0 || admins.length > 0);
  // The top row is your own level: always open, no caret.
  const isTop = node.id === root.id;
  const open = canExpand && isExpanded(node.id);
  const childDepth = skipRow ? depth : depth + 1;
  const r = isPerson ? null : rollup(node);
  const t = TILE[node.type];

  const strategies = FLAG_META.filter(f => granted[f.key]).map(f => {
    const src = flagSource(root, node, f.key, flagOverrides);
    if (!src.on && !src.setHere) return null;
    const title = src.setHere ? `${f.label} set ${src.on ? 'on' : 'off'} here`
      : src.from ? `${f.label} inherited from ${src.from.name}` : `${f.label} on by default`;
    return (
      <span key={f.key} title={title}
        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
          !src.on ? 'border-destructive/30 text-destructive line-through bg-destructive/5'
            : src.setHere ? 'border-primary bg-primary text-white' : 'border-primary/30 text-primary'
        }`}>
        {f.label}
      </span>
    );
  }).filter(Boolean);

  const personStatus = node.status === 'suspended' || node.status === 'pending' ? node.status : null;
  return (
    <>
      {!skipRow && (
      <tr
        id={`tm-row-${node.id}`}
        role="row"
        aria-level={depth + 1}
        aria-expanded={canExpand && !isTop ? open : undefined}
        onClick={() => { if (canExpand && !isTop) onToggle(node.id); }}
        className={`border-b last:border-b-0 transition-colors hover:bg-accent/30 ${canExpand && !isTop ? 'cursor-pointer' : ''}`}
      >
        <td className="py-2 pr-3" style={{ paddingLeft: 12 + depth * 20 }}>
          <div className="flex items-center gap-1.5 min-w-0">
            {canExpand && !isTop
              ? (open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />)
              : <span className="w-3.5 shrink-0" />}
            <div className={`h-6 w-6 rounded-md flex items-center justify-center shrink-0 ${t.bg} ${t.text}`}>
              {React.cloneElement(t.icon as React.ReactElement, { className: 'h-3 w-3' })}
            </div>
            <span className="min-w-0 flex flex-col">
              <span className={`truncate ${isPerson ? 'font-medium' : 'font-semibold'} ${personStatus ? 'text-muted-foreground' : 'text-foreground'}`}>{node.name}</span>
              {showPath && (() => {
                const crumb = (findPath(root, node.id) ?? []).slice(0, -1)
                  .filter(n => n.type !== 'platform').map(n => n.name).join(' › ');
                return crumb ? <span className="text-[11px] text-muted-foreground truncate">{crumb}</span> : null;
              })()}
            </span>
            {personStatus === 'suspended' && <Badge variant="destructive" className="text-[9.5px] px-1.5 py-0">Suspended</Badge>}
            {personStatus === 'pending' && <Badge variant="outline" className="text-[9.5px] px-1.5 py-0 border-amber-300 text-amber-800">Pending</Badge>}
          </div>
        </td>
        <td className="py-2 pr-3 whitespace-nowrap">
          <span className={`text-[10px] font-semibold tracking-wide uppercase px-1.5 py-0.5 rounded ${TYPE_BADGE[node.type].bg} ${TYPE_BADGE[node.type].text}`}>
            {isPerson ? memberLabel(node, parentType) : typeLabel(node.type)}
          </span>
        </td>
        <td className="py-2 pr-3 text-right font-mono tabular-nums text-muted-foreground">{r ? memberCount(node) : '—'}</td>
        <td className="py-2 pr-3 text-right font-mono tabular-nums">{(r ? r.clients : node.clientCount).toLocaleString()}</td>
        <td className="py-2 pr-3"><div className="flex items-center gap-1">{strategies.length ? strategies : <span className="text-muted-foreground">—</span>}</div></td>
        <td className="py-2 pr-4 text-right">
          <Button size="sm" variant="outline" className="h-7 text-xs" data-tour="downline-manage"
            onClick={e => { e.stopPropagation(); onManage(node.id); }}>
            Manage
          </Button>
        </td>
      </tr>
      )}
      {open && (
        <>
          {admins.map(a => {
            const id = `admin:${node.id}:${a.id}`;
            return (
              <tr key={id} id={`tm-row-${id}`} role="row" aria-level={childDepth + 1}
                className="border-b hover:bg-accent/30">
                <td className="py-2 pr-3" style={{ paddingLeft: 12 + childDepth * 20 }}>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-3.5 shrink-0" />
                    <div className="h-6 w-6 rounded-md flex items-center justify-center shrink-0 bg-muted text-[#2D4B6E]">
                      <Shield className="h-3 w-3" />
                    </div>
                    <span className={`truncate font-medium ${a.status === 'suspended' ? 'text-muted-foreground' : 'text-foreground'}`}>{a.name}</span>
                    {a.status === 'suspended' && <Badge variant="destructive" className="text-[9.5px] px-1.5 py-0">Suspended</Badge>}
                  </div>
                </td>
                <td className="py-2 pr-3 whitespace-nowrap">
                  <span className="text-[10px] font-semibold tracking-wide uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    {typeLabel(node.type)} Admin
                  </span>
                </td>
                <td className="py-2 pr-3 text-right text-muted-foreground">—</td>
                <td className="py-2 pr-3 text-right text-muted-foreground">—</td>
                <td className="py-2 pr-3 text-muted-foreground">—</td>
                <td className="py-2 pr-4 text-right">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={e => { e.stopPropagation(); onManage(id); }}>
                    Manage
                  </Button>
                </td>
              </tr>
            );
          })}
          {node.children.map(c => (
            <TreeTableRows
              key={c.id}
              root={root}
              node={c}
              parentType={node.type}
              depth={childDepth}
              isExpanded={isExpanded}
              onToggle={onToggle}
              onManage={onManage}
              granted={granted}
              flagOverrides={flagOverrides}
            />
          ))}
        </>
      )}
    </>
  );
}


function DetailPanel({
  root, node, isRoot, granted, flagOverrides, onToggleFlag, onResetFlag, onSelect, onOpenClients, onAdd, onOpenClient,
  openingClientId, onSuspendToggle, onResetPassword, onRename, onDelete, onMove, onViewAs, onClientBookToggle, onRoleChange,
  viewerIsPlatformStaff = false,
  viewerIsSuperAdmin = false,
}: {
  root: OrgNode;
  node: OrgNode;
  /** The top of the viewer's tree — their own org, or the platform. Not
   *  renamed or deleted from here. */
  isRoot: boolean;
  granted: Record<FlagKey, boolean>;
  flagOverrides: Record<string, boolean>;
  onToggleFlag: (nodeId: string, flag: FlagKey) => void;
  /** Drops the node's own setting so it inherits from above again. */
  onResetFlag: (nodeId: string, flag: FlagKey) => void;
  /** Selects another node in the tree (a child listed in this panel). */
  onSelect: (nodeId: string) => void;
  /** Agents: opens their client grid. */
  onOpenClients: () => void;
  /** Orgs: invite a person or a sub-org under this node. */
  onAdd: () => void;
  onOpenClient: (client: ClientRef) => void;
  /** Client whose scenarios are loading right now — its row shows a spinner. */
  openingClientId?: string | null;
  onSuspendToggle: (n: OrgNode) => void;
  onResetPassword: (n: OrgNode) => void;
  onRename: (n: OrgNode) => void;
  onDelete: (n: OrgNode) => void;
  /** Move a person or an agency/team elsewhere in the downline. Absent = hidden. */
  onMove?: (n: OrgNode) => void;
  /** Super-admin only: one-click impersonation of an agent from their node. */
  onViewAs?: (n: OrgNode) => void;
  /** Platform staff only: toggle whether this member carries a client book. Absent = button hidden. */
  onClientBookToggle?: (n: OrgNode) => void;
  /** Owners and platform staff only: promote an agent to admin, or back.
   *  Absent = the control is hidden. Never offered for an org's owner. */
  onRoleChange?: (n: OrgNode, role: 'agent' | 'org_admin') => void;
  /** Super admin / platform admin viewing: full access to everybody, owners included. */
  viewerIsPlatformStaff?: boolean;
  /** The company (owned by platform staff) is the super admin's to deactivate; nobody else gets the button. */
  viewerIsSuperAdmin?: boolean;
}) {
  const t = TILE[node.type];
  const isAgent = node.type === 'agent';
  // An org owner manages their own account in Settings; nobody in their
  // downline suspends, renames or resets them (Drew, 2026-09-22). Platform
  // staff keep full access to everybody, owners included — set_member_status
  // and rename_member refuse an owner for everyone else (20260922000002).
  const isOrgOwner = isAgent && node.orgRole === 'org_owner';
  const ownerLocked = isOrgOwner && !viewerIsPlatformStaff;
  const directChildren = node.children;
  const suspended = node.status === 'suspended';

  // Granted-from-above drives visibility, so an inherited-off flag can still
  // be overridden per node.
  const visibleFlags = FLAG_META.filter(f => granted[f.key]);

  return (
    <div>
      <div className="px-5 py-4">
        {/* Header */}
        <div className="flex items-center gap-3 min-w-0">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${t.bg} ${t.text}`}>
            {React.cloneElement(t.icon as React.ReactElement, { className: 'h-[18px] w-[18px]' })}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-[15px] font-bold text-foreground truncate leading-tight">{node.name}</h2>
              <span className={`text-[9.5px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded ${TYPE_BADGE[node.type].bg} ${TYPE_BADGE[node.type].text}`}>
                {typeLabel(node.type)}
              </span>
              {suspended && (
                <Badge variant="destructive" className="text-[9.5px] px-1.5 py-0">Suspended</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{isAgent ? `${node.clientCount} clients` : (node.admin ? node.admin + ' · ' : '') + rollupLine(node)}</p>
          </div>
        </div>


        {/* Feature access — boxed toggles side by side, mock-style */}
        {visibleFlags.length > 0 && (
          <div className="grid grid-cols-1 gap-2 mt-4" data-tour="downline-sheet-flags">
            {visibleFlags.map(f => {
              // Say where the value comes from, not just what it is. A level
              // that set its own value can hand control back to its parent.
              const src = flagSource(root, node, f.key, flagOverrides);
              const isOn = src.on;
              const scope = isAgent ? `for ${node.name}` : `for this ${node.type === 'imo' ? 'IMO' : typeLabel(node.type).toLowerCase()} and everyone under it`;
              return (
                <div key={f.key} className={`flex items-start gap-2.5 rounded-md border px-3 py-2 ${isOn ? 'bg-primary/5 border-primary/20' : 'bg-muted/40 border-border'}`}>
                  <Switch
                    checked={isOn}
                    onCheckedChange={() => onToggleFlag(node.id, f.key)}
                    className="scale-[0.8] -mx-0.5 mt-0.5"
                  />
                  <div className="leading-tight min-w-0 flex-1">
                    <p className={`text-xs font-semibold ${isOn ? 'text-primary' : 'text-foreground'}`}>{f.label}</p>
                    <p className="text-[11px] text-muted-foreground">{isOn ? 'On' : 'Off'} {scope}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {src.setHere
                        ? 'Set here'
                        : src.from
                          ? `Inherited from ${src.from.name}`
                          : 'Inherited · on by default'}
                    </p>
                    {src.setHere && (
                      <button
                        type="button"
                        onClick={() => onResetFlag(node.id, f.key)}
                        className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-primary hover:underline"
                      >
                        <RotateCcw className="h-2.5 w-2.5" /> Reset to inherited
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Facts — label left, mono value right, two columns */}
        {!isAgent && (
          <div className="grid grid-cols-2 gap-x-8 mt-4">
            {(() => {
              const children = flattenDownline(node);
              const agentCount = children.filter(c => c.type === 'agent').length;
              const teamCount = children.filter(c => c.type === 'team').length;
              const agencyCount = children.filter(c => c.type === 'agency').length;
              const clientCount = children.filter(c => c.type === 'agent').reduce((s, c) => s + c.clientCount, 0);
              const facts: [string, string][] = [];
              if (agencyCount > 0) facts.push(['Agencies', String(agencyCount)]);
              if (teamCount > 0) facts.push(['Teams', String(teamCount)]);
              facts.push(['Agents', String(agentCount)]);
              facts.push(['Clients', clientCount.toLocaleString()]);
              return facts.map(([l, v]) => (
                <div key={l} className="flex items-center justify-between py-1.5 border-b text-[12.5px]">
                  <span className="text-muted-foreground">{l}</span>
                  <span className="font-mono font-semibold tabular-nums text-foreground">{v}</span>
                </div>
              ));
            })()}
          </div>
        )}

        {/* Actions — visible and role-scoped, not buried in a menu. Delete is
            confirm-guarded; suspend is reversible. */}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          {isAgent ? (
            <Button size="sm" className="h-7 text-xs gap-1" style={{ backgroundColor: '#2D4B6E' }} onClick={onOpenClients} data-tour="downline-open-clients">
              <FolderOpen className="h-3.5 w-3.5" /> Open clients
            </Button>
          ) : addOptions(node.type).length > 0 && (
            <Button size="sm" className="h-7 text-xs gap-1" onClick={onAdd} data-tour="downline-sheet-add">
              <Plus className="h-3.5 w-3.5" /> {node.type === 'platform' ? 'New IMO' : `Add to ${typeLabel(node.type)}`}
            </Button>
          )}
          {isAgent && onViewAs && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-violet-400 text-violet-700 hover:bg-violet-50" onClick={() => onViewAs(node)} data-tour="downline-view-as">
              <Shield className="h-3.5 w-3.5" /> View as
            </Button>
          )}
          {isAgent && !ownerLocked && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onResetPassword(node)} data-tour="downline-reset-password">
              <KeyRound className="h-3.5 w-3.5" /> Reset password
            </Button>
          )}
          {!ownerLocked && !isRoot && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onRename(node)} data-tour="downline-account">
              <Pencil className="h-3.5 w-3.5" /> {isAgent ? 'Edit name' : 'Rename'}
            </Button>
          )}
          {isAgent && !ownerLocked && (
            <Button size="sm" variant="outline"
              className={`h-7 text-xs gap-1 ${suspended ? 'border-emerald-400 text-emerald-700 hover:bg-emerald-50' : 'border-destructive/40 text-destructive hover:bg-destructive/10'}`}
              onClick={() => onSuspendToggle(node)} data-tour="downline-suspend">
              {suspended ? 'Reactivate' : 'Suspend'}
            </Button>
          )}
          {/* Promote / demote. An org's owner is never offered it — the RPC
              refuses that target anyway, since demoting an owner would orphan
              the org. Their clients are untouched either way. */}
          {isAgent && onRoleChange && node.orgRole !== 'org_owner' && (
            <Select
              value={node.orgRole === 'org_admin' ? 'org_admin' : 'agent'}
              onValueChange={v => onRoleChange(node, v as 'agent' | 'org_admin')}
            >
              <SelectTrigger className="h-7 w-[7.5rem] text-xs" title="Their role here. Changing it does not move their clients." data-tour="downline-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">Agent</SelectItem>
                <SelectItem value="org_admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          )}
          {isAgent && onClientBookToggle && node.orgRole !== 'org_owner' && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
              title={node.hasClientBook === false ? 'Currently no own clients: they work from All Clients' : 'Currently has own clients'}
              onClick={() => onClientBookToggle(node)} data-tour="downline-client-book">
              {node.hasClientBook === false ? 'Give own clients' : 'No own clients'}
            </Button>
          )}
          {/* Deactivate: a member (deactivate_member) or an org
              (BranchActionDialog asks "all of it, or let me choose?"). Never
              on an org owner's tile — deactivate_member refuses an owner for
              every caller; an owner goes when their org is deactivated.
              Downline has no Delete: that is the Admin Panel's, platform
              staff only (Drew, 2026-09-29). */}
          {/* Move: a person (never an owner: their org moves instead) or an
              agency/team with everything under it. IMOs don't move. */}
          {onMove && !isRoot && !isOrgOwner && node.type !== 'imo' && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onMove(node)} data-tour="downline-move">
              Move
            </Button>
          )}
          {!isOrgOwner && !isRoot && !(node.staffOwned && !viewerIsSuperAdmin) && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => onDelete(node)} data-tour="downline-deactivate">
              <UserX className="h-3.5 w-3.5" /> {isAgent ? 'Deactivate' : 'Deactivate org'}
            </Button>
          )}
        </div>

        {/* Pending invite banner */}
        {node.status === 'pending' && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-4">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
            <span className="text-xs text-amber-800 flex-1">Invite pending — hasn't been accepted yet.</span>
          </div>
        )}

        {/* Members / children list */}
        <div className="mt-5 pt-4 border-t">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11.5px] font-semibold tracking-wide uppercase text-muted-foreground">
              {isAgent ? 'Recent clients' : `${typeLabel(directChildren[0]?.type ?? 'agent')}s`}
            </p>
          </div>
          <div className="space-y-0.5">
            {(isAgent ? node.clients.slice(0, 5) : directChildren).map((m: OrgNode | ClientRef) => {
              const memberNode = isAgent ? null : (m as OrgNode);
              const memberName = m.name;
              const memberSub = isAgent
                ? `Edited ${(m as ClientRef).lastEdit}`
                : rollupLine(m as OrgNode);
              const mt = memberNode ? TILE[memberNode.type] : null;
              return (
                <div
                  key={m.id}
                  onClick={() => {
                    if (memberNode) onSelect(memberNode.id);
                    else onOpenClient(m as ClientRef);
                  }}
                  className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-accent/30 cursor-pointer"
                >
                  {mt ? (
                    <div className={`h-[30px] w-[30px] rounded-full flex items-center justify-center shrink-0 ${mt.bg} ${mt.text}`}>
                      {React.cloneElement(mt.icon as React.ReactElement, { className: 'h-3.5 w-3.5' })}
                    </div>
                  ) : (
                    <div className="h-[30px] w-[30px] rounded-full bg-muted flex items-center justify-center shrink-0 text-[11px] font-bold text-[#2D4B6E]">
                      {openingClientId === m.id
                        ? <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
                        : initials(memberName)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{memberName}</p>
                    <p className="text-[11.5px] text-muted-foreground truncate">{memberSub}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page wrapper — renders AppShell with sidebar
// ---------------------------------------------------------------------------
function PageWrapper({
  children, navigate,
}: {
  children: React.ReactNode;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <AppShell
      sidebar={
        <AppSidebar
          currentView={'team-management' satisfies AppView}
          wizardStep={0}
          visitedSteps={new Set()}
          onViewChange={(view: AppView) => {
            // Every sidebar navItem routes through here. Hand the target view
            // to Index via router state so Profile/Team/Billing/etc. actually
            // navigate instead of silently no-opping on this page.
            if (view !== 'team-management') navigate('/', { state: { initialView: view } });
          }}
          onWizardStepChange={() => {}}
          onNewScenario={() => {}}
          scenarioCount={0}
        />
      }
      headerSlot={<div id="tm-header-slot" className="flex items-center gap-1 min-w-0 flex-1" />}
    >
      {/* Own route, own provider: the Downline walkthrough plays here. The full
          tour ends here for owners and admins; Get started takes them home. */}
      <TutorialProvider view="team-management" wizardStep={0} onTourFinished={() => navigate('/')}>
        {children}
      </TutorialProvider>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Standalone route (/team-management) — org owners/admins and platform admins.
// Super admins never see this page: Team Management lives inside their Admin
// Panel (the Network rail item renders TeamManagementContent directly), so the
// standalone shell redirects them there. Effective roles keep this
// impersonation-safe — while viewing as someone else you get their page.
// ---------------------------------------------------------------------------
export default function TeamManagementPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  if (isSuperAdmin) return <Navigate to="/super-admin" replace />;
  return (
    <PageWrapper navigate={navigate}>
      <TeamManagementContent />
    </PageWrapper>
  );
}
